<script>
import {mapActions, mapState} from 'vuex';
import ProgramSummaryCard from '@/app/elements/components/ProgramSummaryCard.vue';
import {isArray} from "@incodeapps/js-utilities";
import ApplicationConfig from "@/app/application/components/applications/config.json";
import { saveApplication } from "@/app/api";
import checkMark from "@/assets/img/icons/check-mark.svg";
import exclamationMark from "@/assets/img/icons/exclamation-mark.svg";
import garbageCan from "@/assets/img/icons/garbage-can.svg";

export default {
	components: {
		ProgramSummaryCard
	},
	filters: {
		formatMoney(value) {
			return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, }).format(value);
		},
	},
	data() {
		return {
			//icons
			checkMark: checkMark,
			exclamationMark: exclamationMark,
			garbageCan: garbageCan,
			//values
			applicant: {},
			equipments: [],
			coApps: [],
			programType: '',
			app: null,
			note: '',
			isComplete: false,
			vModels: {}
		};
	},
	computed: {
		...mapState({
			applicationSections: state => state.application.sections,
			authUserVendor: state => state.auth.authUserVendor,
			authUser: state => state.auth.authUser,
			locale: state => state.app.locale,
			logo: state => state.auth.logo,
			optionSets: state => state.application.options
		}),
		formsJson() {
			return ApplicationConfig;
		},
		isLeaseProgram() {
			const code = this.vModels?.program?.program?.Code;
			return Number(code) === 2;
		},
		maxCoApplicants() {
			return this.isLeaseProgram ? 1 : 3;
		},
		isMaxApplicants() {
			const coAppCount = Array.isArray(this.coApps) ? this.coApps.length : 0;
			return coAppCount >= this.maxCoApplicants;
		}
	},
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		goToStep: {
			type: Function,
			default: () => {}
		},
		nextStep: {
			type: Boolean,
			default: false
		},
		setNextDisabled: {
			type: Function,
			default: () => {}
		},
		setNextPopoverText: {
			type: Function,
			default: () => {}
		},
		type: {
			type: String,
			default: ''
		}
	},
	watch: {
		"isComplete": function (val) {
			this.setNextDisabled(!val);
		},
		"nextStep": "onSubmit"
	},
	async mounted() {
		this.saveLoading(true);
		this.app = this.application;
		this.applicant = this.makeApplicantObject(this.getApplicant());
		if (this.app.application.equipments) {
			this.equipments = this.app.application.equipments
		}
		if (this.app.application.hasOwnProperty('note')) {
			this.note = this.app.application.note
		}
		this.vModels.program = this.app.application.program ?? {};
		this.coApps = this.app.application.coApps;
		if (this.vModels?.program?.program) {
			this.programType = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)} Payment`;
		} else if (this.app.application.isImportedFromEdge) {
			this.programType = 'Imported from EDGE, no program details found'
		}
		this.isComplete = this.checkApplication(this.app.application);
	},
	methods: {
		...mapActions([
			'removeAlerts',
			'saveLoading',
			'showErrorAlert',
			'showSuccessAlert'
		]),
		getApplicant() {
			return this.app?.application;
		},
		makeApplicantObject(data) {
			return {
				address1: data?.address1,
				address2: data?.address2,
				city: data?.city,
				country: data?.country,
				dob: data?.dob,
				email: data?.email ,
				employer: data?.employer,
				employmentTerm: data?.employmentTerm,
				employmentTitle: data?.employmentTitle,
				firstName: data?.firstName ?? '',
				grossIncome: data?.grossIncome,
				lastName: data?.lastName ?? '',
				linkPurpose: data?.linkPurpose,
				linkSentOn: data?.linkSentOn,
				middleInitial: data?.middleInitial,
				postal: data?.postal,
				primaryPhone: data?.primaryPhone,
				province: data?.province,
				titleOwner: data?.titleOwner
			}
		},
		checkApplication(data) {
			let result = {
				complete: true,
				errorMessages: []
			};
			const applicantComplete = this.checkApplicant(this.getApplicant());
			if (!applicantComplete) {
				result.complete = false;
				result.errorMessages.push('Complete applicant information');
			}
			if (isArray(data.coApps)) {
				for (const coApplicant of data.coApps) {
					const coApplicantComplete = this.checkApplicant(coApplicant);
					if (!coApplicantComplete) {
						result.complete = false;
						result.errorMessages.push('Complete co-applicant information');
						break;
					}
				}
			}
			if ((data?.equipments?.length ?? 0) < 1) {
				result.complete = false;
				result.errorMessages.push('Add at least one equipment');
			}
			this.saveLoading(false);
			this.setNextPopoverText(result.errorMessages.join("\n"));
			return result.complete;
		},
		checkApplicant(applicant) {
			let result = true;
			if (!applicant) {
				result = false;
			}
			let required = [
				'address1',
				'city',
				'dob',
				'employer',
				'employmentTerm',
				'employmentTitle',
				'firstName',
				'grossIncome',
				'lastName',
				'postal',
				'primaryPhone',
				'province',
				'titleOwner',
			]
			for (const field of required) {
				if ((applicant?.[field]?.trim?.() || '-') === '-') {
					result = false;
					break;
				}
			}
			return result;
		},
		async removeApplicant(index) {
			this.saveLoading(true);
			this.coApps.splice(index, 1);
			await saveApplication({
				id: this.$route.params.applicationId,
				coApps: this.coApps,
				errors: this.errors
			});
			this.isComplete = this.checkApplication(this.app.application);
		},
		async removeEquipment(index) {
			this.saveLoading(true);
			this.equipments.splice(index, 1);
			await saveApplication({
					id: this.$route.params.applicationId,
					equipments: this.equipments
			});
			this.isComplete = this.checkApplication(this.app.application);
		},
		async saveNote() {
			this.saveLoading(true);
			await saveApplication({
					id: this.$route.params.applicationId,
					note: this.vModels.note
			});
			this.app.application.note = this.vModels.note
			this.note = this.vModels.note
			this.saveLoading(false);
		},
		async resetNote() {
			this.vModels.note = this.app.application.note;
			this.note = this.app.application.note;
		},
		async onSubmit() {
			if (this.nextStep) {
				this.saveLoading(true);
				await this.removeAlerts();
				this.goToStep({ routeName: "Terms", params: { applicationId: this.$route.params.applicationId } });
			}
		}
	}
};
</script>

<template>
	<div class="application-dashboard-template">
		<div class="container-xl">
			<div class="row">
				<div class="col-24">
					<div class="template__page-title">
						<h1>{{ applicant?.address1 ? applicant.address1 : 'Dashboard' }}</h1>
					</div>
				</div>
			</div>
		</div>
		<form @submit.prevent="onSubmit" novalidate>
			<div class="container-xl template--vh">
				<div class="row">
					<div class="col-lg-8 d-none">
						<router-link tag="div" :to="{ name: `${type}Equipment`, params: $route.params }">
							<button type="button"
							        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid w-100 mb-4"
							        color="primary">
								Add Note
							</button>
						</router-link>
					</div>
					<div class="col-lg-8">
						<ProgramSummaryCard
							:program="vModels.program"
							:program-type="programType"
							:clickable="true"
							:click-handler="() => goToStep({ name: 'calculator' })"
						/>
						<div class="tffi-card tffi-card-summary-card">
							<div v-if="!vModels?.program?.leaseTermMonths && authUser.usr_ShowDealerCosts" class="tffi-card-summary-card__bottom-area">
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Cost Rate: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">{{ vModels.program?.dealerCostRate | formatMoney }}%</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Cost: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.program?.dealerCost | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Admin Fee: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.program?.dealerAdminFee | formatMoney }}</p>
								</div>
							</div>
						</div>
						<div @click="resetNote" data-bs-backdrop="static" data-bs-keyboard="false" data-bs-toggle="modal"
						     data-bs-target="#notesModal" v-if="note" class="tffi-card tffi-card-summary-card sticky-note theming">
							<p class="strong">Notes</p>
							{{ note }}
						</div>
						<button v-if="!note" type="button" class="tffi-btn tffi-btn--solid theming accent-color w-100 fw-bold mb-4"
						        data-bs-toggle="modal" data-bs-target="#notesModal">
							Add Note
						</button>
						<!-- Modal -->
						<div class="modal fade show-" id="notesModal" tabindex="-1" aria-labelledby="notesModalLabel"
						     aria-hidden="true">
							<div class="modal-dialog modal-dialog-centered">
								<div class="modal-content">
									<div class="modal-body">
										<form-field v-model="vModels.note" name="note" bootstrap multiline type="textarea"
										            rows="5"></form-field>
									</div>
									<div class="modal-footer d-flex justify-content-between" style="flex-direction: row !important;">
										<button data-bs-dismiss="modal" type="button"
										        class="tffi-btn tffi-btn--solid theming accent-color fw-bold"
										        style="flex: 0 0 auto; width: auto;">Cancel</button>
										<button @click="saveNote" data-bs-dismiss="modal" type="button"
										        class="tffi-btn tffi-btn--solid theming primary-color fw-bold"
										        style="flex: 0 0 auto; width: auto;">Save Note</button>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col-lg-8">
						<div style="cursor: pointer;"
						     @click="goToStep({ name: 'applicants', query: { select: 'main' }})" class="tffi-card tffi-card-secondary-card theming primary-color person">
							<div class="d-none- tffi-card-secondary-card__left-area">
								<div class="tffi-card-secondary-card__left-area__status-indicator">
									<div v-if="checkApplicant(applicant)" class="status-accepted">
										<img :src="checkMark" alt="">
									</div>
									<div v-else class="status-denied">
										<img :src="exclamationMark" alt="">
									</div>
								</div>
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Applicant</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>{{ applicant.firstName + ' ' + (applicant.middleInitial ?
										applicant.middleInitial + ' ' : '') + applicant.lastName }}</p>
									<p>{{ applicant.email }}</p>
								</div>
								<button @click="removeApplicant('main')"
								        class="tffi-card-secondary-card__right-area__trash-area d-none">
									<img :src="garbageCan" alt="">
								</button>
							</div>
						</div>
						<div style="cursor: pointer;"
						     @click="goToStep({ name: 'applicants', query: { select: index.toString() }})"
						     v-if="coApps" v-for="(coApp, index) in this.coApps" class="tffi-card tffi-card-secondary-card person">
							<div class="d-none- tffi-card-secondary-card__left-area">
								<div class="tffi-card-secondary-card__left-area__status-indicator">
									<div v-if="checkApplicant(coApps[index])" class="status-accepted">
										<img :src="checkMark" alt="">
									</div>
									<div v-else class="status-denied">
										<img :src="exclamationMark" alt="">
									</div>
								</div>
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Co-Applicant</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>{{ (coApp.firstName ?? '') + ' ' + (coApp.middleInitial ?? '') + ' ' + (coApp.lastName ?? '') }}</p>
									<p>{{ coApp.email }}</p>
								</div>
								<button @click="removeApplicant(index)"
								        class="tffi-card-secondary-card__right-area__trash-area d-none-">
									<img :src="garbageCan" alt="">
								</button>
							</div>
						</div>
							<button type="button"
							        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid w-100 mb-4"
											@click="goToStep({ name: 'applicants', query: { newApplicant: '1' } })"
							        color="primary">
								Add Applicant
							</button>
					</div>
					<div class="col-lg-8">
						<div v-if="equipments" v-for="(equipment, index) in equipments" class="tffi-card tffi-card-secondary-card">
							<div class="tffi-card-secondary-card__left-area">
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Equipment</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>Type: {{ equipment['equipmentType'] }}</p>
									<p>Brand: {{ equipment['brand'] }}</p>
									<p>Model: {{ equipment['model'] }}</p>
								</div>
								<button @click="removeEquipment(index)" class="tffi-card-secondary-card__right-area__trash-area">
									<img :src="garbageCan" alt="">
								</button>
							</div>
						</div>
							<button type="button"
							        class="tffi-btn tffi-btn--solid tffi-btn--bold theming accent-color primary-color-btn-solid w-100 mb-4"
											@click="() => goToStep({ name: 'equipment' })"
							        color="primary"
							        v-if="equipments.length < 4">
								Add Equipment
							</button>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>
.edit-btn {
	position: relative;
	top: -1rem;
	right: -1rem
}
.card-body {
	.form-group.static-form-group {
		&:last-child {
			border-bottom: none;
			margin-bottom: 0;
		}
	}
	.invalid-feedback {
		margin-top: -1rem;
		margin-bottom: 1rem;
	}
}
.form-control-plaintext {
	white-space: pre-line;
}
.tffi-card-secondary-card.person,
.tffi-card-secondary-card.person {
	&:hover {
		border: 2px solid #C18F14;
	}
}
.sticky-note {
	padding: 40px;
	padding-top: 20px;
	overflow-wrap: anywhere;
	cursor: pointer;
	background-color: #DFCE86;
	border: 2px solid transparent;

	&:hover {
		border: 2px solid #C18F14;
	}
}
.cursor-pointer {
	cursor: pointer;
	&:hover {
		box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
	}
}
</style>
